import type { ReactNode } from "react";
import { Activity, LockKeyhole, MessageCircle } from "lucide-react";
import { Button } from "../../components/ui/button/button.tsx";
import LinkifiedText from "./linkified-text.tsx";
import styles from "./request-activity.module.css";

export type ActivityNote = {
  id: number;
  employeeId?: number;
  employeeName: string | null;
  noteType: string;
  comments: string;
  updatedBy?: number;
  updatedAt: string | null;
};

type Props = {
  notes: ActivityNote[];
  audience: "employee" | "manager";
  className?: string;
  filters?: ReactNode;
  loading: boolean;
  error: string;
  onRetry: () => void;
  employeeId?: number;
  ownerId?: number | null;
};

function noteDate(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
}

function activityVerb(type: string) {
  if (type === "INTERNAL_NOTE") return "added an internal note";
  if (type === "REQUEST_INFO") return "requested information";
  if (type === "RECORD_OUTCOME") return "recorded an outcome";
  return "shared an update";
}

export default function RequestActivity({ notes, audience, className, filters, loading, error, onRetry, employeeId, ownerId }: Props) {
  const isManager = audience === "manager";
  return <section className={className} aria-labelledby="request-activity-title">
    <div className={styles.headingRow}>
      <div className={styles.heading}><span className={styles.headingIcon}><MessageCircle size={18} aria-hidden /></span><div>
        <h2 id="request-activity-title">Discussion &amp; activity</h2>
        <p>{isManager ? "Employee-visible updates and private coordination are kept separate." : "Only information shared with you appears here."}</p>
      </div></div>
      {filters}
    </div>
    {loading ? <p className={styles.state} role="status">{isManager ? "Loading activity..." : "Loading updates…"}</p>
      : error ? <div className={styles.state}><p role="alert">{error}</p><Button variant="muted-outline" size="sm" onClick={onRetry}>{isManager ? "Retry activity" : "Retry updates"}</Button></div>
      : notes.length ? <ul className={styles.events}>{notes.map((note) => {
        const internal = note.noteType === "INTERNAL_NOTE";
        const name = note.employeeName || (isManager ? "Someone" : "Growth & Initiatives");
        const authorId = note.employeeId ?? note.updatedBy;
        const authorRole = authorId === employeeId ? "You" : ownerId != null && authorId === ownerId ? "Owner" : "Growth team";
        const initials = note.employeeName?.trim().split(/\s+/).map((part) => part[0]).slice(0, 2).join("").toUpperCase();
        return <li key={note.id} className={styles.event}>
          <span className={styles.avatar} aria-hidden>{internal ? <LockKeyhole size={17} /> : initials || (isManager ? <Activity size={17} /> : "GI")}</span>
          <div className={styles.eventContent}>
            {isManager ? <span className={styles.visibility}>{internal ? "Internal only" : "Employee-visible"}</span> : null}
            <strong>{name} {isManager ? activityVerb(note.noteType) : "shared an update"}</strong>
            <div className={styles.meta}>
              {!isManager ? <span className={styles.authorRole}>By: {authorRole}</span> : null}
              {note.updatedAt ? <small><time dateTime={note.updatedAt}>{noteDate(note.updatedAt)}</time></small> : null}
            </div>
            <p className={styles.message}><LinkifiedText text={note.comments} /></p>
          </div>
        </li>;
      })}</ul> : <p className={styles.state}>{isManager ? "No activity in this view yet." : "No updates have been shared yet."}</p>}
  </section>;
}
